import { isAxiosError } from "axios";

/**
 * Every failed HTTP call to the Docket API rejects with an ApiError. Its message is the one the
 * React app showed users: the response body's `error` (or `errors`, joined), or "Communication
 * Error: …" when the body has neither.
 */
export class ApiError extends Error {
  override readonly name = "ApiError";
  /** The HTTP status, or undefined when no response arrived. */
  readonly status: number | undefined;

  constructor(message: string, options: { status?: number; cause?: unknown } = {}) {
    super(message, { cause: options.cause });
    this.status = options.status;
  }
}

/** Convert an axios error into an ApiError. Anything else (and existing ApiErrors) passes through. */
export function toApiError(err: unknown): unknown {
  if (!isAxiosError(err)) {
    return err;
  }
  const data: unknown = err.response?.data;
  const body =
    typeof data === "object" && data !== null ? (data as Record<string, unknown>) : undefined;
  const bodyError = body?.["error"] || body?.["errors"];

  let message: string;
  if (!bodyError) {
    message = `Communication Error: ${err.message}`;
  } else if (Array.isArray(bodyError)) {
    message = bodyError.join(", ");
  } else {
    // In practice a string. (The React app printed objects as "[object Object]"; JSON at least says something.)
    message = typeof bodyError === "string" ? bodyError : JSON.stringify(bodyError);
  }
  return new ApiError(message, { status: err.response?.status, cause: err });
}

/**
 * The message to show for a failed call: the API's own, or `fallback` for failures that never got a
 * response from it (e.g. there were no tokens to send).
 */
export function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}
